<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useAddIdentifierDialog } from '../../composables/useAddIdentifierDialog';

const { t } = useI18n();

// Props
interface Props {
    visible: boolean;
    selectedLanguages: string[];
    loading?: boolean;
    validateIdentifier: (identifier: string) => { valid: boolean; message: string };
}

const props = withDefaults(defineProps<Props>(), {
    loading: false
});

// Emits
interface Emits {
    (e: 'update:visible', value: boolean): void;
    (e: 'confirm', data: { identifier: string; texts: Record<string, string> }): void;
    (e: 'cancel'): void;
}

const emit = defineEmits<Emits>();

// Use the composable
const {
    localIdentifier,
    localTexts,
    isFormValid,
    getFormData,
    setupVisibilityWatcher,
    setupLanguagesWatcher
} = useAddIdentifierDialog();

// Computed
const computedIsVisible = computed({
    get: () => props.visible,
    set: (value) => emit('update:visible', value)
});

const canAdd = computed(() => {
    return isFormValid(props.validateIdentifier, props.selectedLanguages);
});

// Watchers
setupVisibilityWatcher(() => props.visible);
setupLanguagesWatcher(() => props.selectedLanguages);

// Watch for visibility changes to initialize form
watch(() => props.visible, (newVisible) => {
    if (newVisible) {
        // Initialize text inputs for selected languages
        const newTexts: Record<string, string> = {};
        for (const langCode of props.selectedLanguages) {
            newTexts[langCode] = '';
        }
        localTexts.value = newTexts;
    }
});

// Methods
function handleConfirm() {
    if (!canAdd.value) return;

    const formData = getFormData();
    emit('confirm', formData);
}

function handleCancel() {
    emit('cancel');
    emit('update:visible', false);
}
</script>

<template>
    <Dialog
        v-model:visible="computedIsVisible"
        modal
        :header="t('textMaintenance.addItemDialog.title')"
        :style="{ width: '600px' }"
        :closable="true"
        data-cy="text-maintenance_add-item-dialog"
        @hide="handleCancel"
    >
        <div class="flex flex-col gap-6">
            <!-- Identifier Input -->
            <div class="mb-4">
                <label for="new-identifier" class="block text-sm font-medium mb-2">
                    {{ t("textMaintenance.addItemDialog.identifier") }}
                </label>
                <InputText
                    id="new-identifier"
                    v-model="localIdentifier"
                    :placeholder="t('textMaintenance.addItemDialog.identifierPlaceholder')"
                    class="w-full"
                    :class="{ 'p-invalid': localIdentifier && !validateIdentifier(localIdentifier).valid }"
                    data-cy="text-maintenance_new-identifier-input"
                    autofocus
                />
                <small v-if="localIdentifier && !validateIdentifier(localIdentifier).valid" class="p-error block mt-1">
                    {{ validateIdentifier(localIdentifier).message }}
                </small>
            </div>

            <!-- Text Inputs for Selected Languages -->
            <div v-if="selectedLanguages.length > 0">
                <div class="block text-sm font-medium mb-2">
                    {{ t("textMaintenance.addItemDialog.texts") }}
                </div>
                <div class="grid grid-cols-12 gap-4">
                    <div v-for="langCode in selectedLanguages" :key="langCode" class="col-span-12 md:col-span-6">
                        <label :for="`text-${langCode}`" class="block text-xs font-medium mb-1 text-content-muted">
                            {{ t(`languageNames.${langCode}`) || langCode.toUpperCase() }}
                        </label>
                        <InputText
                            :id="`text-${langCode}`"
                            v-model="localTexts[langCode]"
                            :placeholder="t('textMaintenance.addItemDialog.textPlaceholder', { language: langCode.toUpperCase() })"
                            class="w-full"
                            :data-cy="`text-maintenance_new-text-input-${langCode}`"
                        />
                    </div>
                </div>
            </div>

            <!-- No Languages Selected Warning -->
            <div v-else class="p-4 border rounded border-yellow-500 bg-yellow-50 text-yellow-800">
                <i class="pi pi-exclamation-triangle mr-2"></i>
                {{ t('textMaintenance.addItemDialog.noLanguagesSelected') }}
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button 
                    :label="t('buttons.cancel')" 
                    severity="secondary" 
                    @click="handleCancel"
                    data-cy="text-maintenance_add-item-cancel-button"
                />
                <Button
                    :label="t('buttons.add')"
                    :disabled="!canAdd"
                    @click="handleConfirm"
                    :loading="loading"
                    data-cy="text-maintenance_add-item-confirm-button"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.field {
    margin-bottom: 0;
}

.p-invalid {
    border-color: #e24c4c;
}

.p-error {
    color: #e24c4c;
    font-size: 0.75rem;
}
</style>